import React, {Dispatch, SetStateAction} from 'react';
import Controls from '../../../controls';
import moment from 'moment';
import Datetime from 'react-datetime';
import {Gender} from 'types/nonMember';
import {PublicEventType} from 'enums/public_event_type';
import site from 'settings/site';

interface Setters {
  setDateOfBirth: Dispatch<SetStateAction<string>>;
  setGender: Dispatch<SetStateAction<Gender | undefined>>;
  setGuardianName: React.Dispatch<React.SetStateAction<string>>;
  setGuardianRelationship: Dispatch<SetStateAction<string | undefined>>;
  setGuardianMobile: Dispatch<SetStateAction<string | undefined>>;
  setGuardianHomeNumber: Dispatch<SetStateAction<string | undefined>>;
  setGuardianEmail: Dispatch<SetStateAction<string | undefined>>;
  setGuardianMailingAddress: Dispatch<SetStateAction<string | undefined>>;
  setIsAgreeToBringGuardian: React.Dispatch<React.SetStateAction<'true' | 'false'>>;
}

interface PersonalDetailsEditFieldsProps {
  setters: Setters;
  fields: any;
  type: PublicEventType;
  hasError: (field: string) => boolean;
  onChange<T>(
    setter: React.Dispatch<React.SetStateAction<T>>,
    field?: string
  ): (e: string | moment.Moment | React.ChangeEvent<HTMLInputElement>) => void;
}

export default function PersonalDetailsEditFields({
  fields,
  setters,
  type,
  hasError,
  onChange,
}: Readonly<PersonalDetailsEditFieldsProps>) {
  const memberAge = fields.age ? fields.age : moment().diff(moment(fields.dateOfBirth, 'YYYY-MM-DD'), 'years');

  const getClassByValidation = (field: string, classes?: string) => {
    return `${classes ?? 'form-control'} ${hasError(field) ? 'border border-danger' : ''}`;
  };

  return (
    <>
      <div className="form-group row">
        <label className="col-sm-3 col-form-label">Date of Birth</label>
        <div className="col-sm-3">
          <Datetime
            className={getClassByValidation('dateOfBirth', '')}
            onChange={onChange(setters.setDateOfBirth, 'dateOfBirth')}
            value={fields.dateOfBirth || ''}
            dateFormat="YYYY-MM-DD"
            timeFormat={false}
            closeOnSelect={true}
            initialViewMode={'years'}
            inputProps={{
              placeholder: 'Date Of Birth',
              className: 'form-control',
            }}
          />
        </div>
        <div className="col-sm-2 pl-0 d-flex justify-content-center flex-column">
          {fields.age ? (
            <>
              <span className="small">Age: {fields.age}</span>
              {memberAge && +fields.age !== +memberAge ? (
                <span className="small text-danger">Selected age: {memberAge}</span>
              ) : null}
            </>
          ) : null}
        </div>

        <label className="col-sm-1 col-form-label text-nowrap">Gender</label>
        <div className="col-sm-2">
          <div className="form-inline">
            <div className={getClassByValidation('gender', 'form-check form-check-inline mb-0 mt-1')}>
              <label className="custom-control custom-checkbox text-content">
                <input
                  className="custom-control-input"
                  type="checkbox"
                  checked={fields.gender === 'Male'}
                  onChange={onChange(setters.setGender)}
                  value="Male"
                />
                <span className="custom-control-label">Male</span>
              </label>
            </div>
          </div>
          <div className="form-inline">
            <div className={getClassByValidation('gender', 'form-check form-check-inline mb-0 mt-1')}>
              <label className="custom-control custom-checkbox text-content">
                <input
                  className="custom-control-input"
                  type="checkbox"
                  checked={fields.gender === 'Female'}
                  onChange={onChange(setters.setGender)}
                  value="Female"
                />
                <span className="custom-control-label">Female</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {memberAge < site.ageOfMajority && (
        <div>
          <legend className="my-3">
            Parent / Guardian &nbsp;
            <small className="text-muted">(Must be completed if the member is under 21 years of age)</small>
          </legend>

          <div className="form-group row">
            <label className="col-sm-3 col-form-label">Name</label>
            <div className="col-sm-3">
              <input
                value={fields.guardianName}
                className={getClassByValidation('guardianName')}
                placeholder="Name"
                onChange={onChange(setters.setGuardianName, 'guardianName')}
              />
            </div>
            <label className="col-sm-3 col-form-label">Relationship</label>
            <div className="col-sm-3">
              <input
                value={fields.guardianRelationship || ''}
                className={getClassByValidation('guardianRelationship')}
                placeholder="Relationship"
                onChange={onChange(setters.setGuardianRelationship, 'guardianRelationship')}
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-sm-3 col-form-label">Mobile Phone No.</label>
            <div className="col-sm-3">
              <Controls.PhoneNumberInput
                isValid={!hasError('guardianMobile')}
                number={fields.guardianMobile}
                placeholder="Mobile Number"
                onChange={onChange(setters.setGuardianMobile, 'guardianMobile')}
              />
            </div>
            <label className="col-sm-3 col-form-label">Home Phone No.</label>
            <div className="col-sm-3">
              <Controls.PhoneNumberInput
                number={fields.guardianHomeNumber}
                placeholder="Home Number"
                onChange={onChange(setters.setGuardianHomeNumber)}
              />
            </div>
          </div>

          <div className="form-group row">
            <label className="col-sm-3 col-form-label">E-mail Address</label>
            <div className="col-sm-3">
              <input
                value={fields.guardianEmail || ''}
                className={getClassByValidation('guardianEmail')}
                placeholder="E-mail Address"
                onChange={onChange(setters.setGuardianEmail)}
              />
            </div>
          </div>

          <div className="form-group row mb-0">
            <label className="col-sm-3 col-form-label">Mailing Address</label>
            <div className="col-sm-9">
              <input
                value={fields.guardianMailingAddress || ''}
                className="form-control"
                placeholder="Mailing Address"
                onChange={onChange(setters.setGuardianMailingAddress)}
              />
            </div>
          </div>

          <div className="form-inline">
            <div className={getClassByValidation('isAgreeToBringGuardian', 'form-check form-check-inline mb-0 mt-1')}>
              <label className="custom-control custom-checkbox text-content">
                <input
                  className="custom-control-input"
                  type="checkbox"
                  checked={fields.isAgreeToBringGuardian === 'true'}
                  onChange={onChange(setters.setIsAgreeToBringGuardian, 'isAgreeToBringGuardian')}
                  value={fields.isAgreeToBringGuardian === 'true' ? 'false' : 'true'}
                />
                <span className="custom-control-label text-danger">
                  I must bring my parent/guardian physically to the event to sign a consent form before I'm allowed to
                  participate.
                </span>
              </label>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
